import React, { useState, useEffect, useRef } from 'react';
import HealthScore from '../components/HealthScore';
import AlertCard from '../components/AlertCard';
import CADocuments from './CADocuments';
import * as cache from '../lib/cache';

/*
  LifeFinance — full three-column page (sidebar / chat+tabs / right panel).

  Props received from App.js:
    user, navigateTo, goBack
    financeProfile, setFinanceProfile
    financeDocuments, setFinanceDocuments
    financeHealthScore, financeScoreLoading
    fetchFinanceProfile, fetchFinanceDocuments, fetchHealthScore, fetchTaxEstimate
    taxData
    lifeFinanceFirstTime, setLifeFinanceFirstTime
*/
const LifeFinance = ({
  user,
  navigateTo,
  goBack,
  financeProfile,
  setFinanceProfile,
  financeDocuments,
  setFinanceDocuments,
  financeHealthScore,
  financeScoreLoading,
  fetchFinanceProfile,
  fetchFinanceDocuments,
  fetchHealthScore,
  fetchTaxEstimate,
  taxData,
  lifeFinanceFirstTime,
  setLifeFinanceFirstTime,
  initialTab,
  onTabConsumed,
}) => {

  // ── Page-local state ────────────────────────────────────────────────────────
  const [financeMessages, setFinanceMessages] = useState([]);
  const [financeInput, setFinanceInput] = useState('');
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeConversations, setFinanceConversations] = useState([]);
  const [financeConversationId, setFinanceConversationId] = useState(null);
  const [financeConvsLoading, setFinanceConvsLoading] = useState(false);
  const [financeMessagesLoading, setFinanceMessagesLoading] = useState(false);
  const [financeChatError, setFinanceChatError] = useState('');
  const [financeUploadStatus, setFinanceUploadStatus] = useState(null);
  const [financeUploadMessage, setFinanceUploadMessage] = useState('');
  const [financeDeleteConvConfirm, setFinanceDeleteConvConfirm] = useState(null);
  const [financeDeleteDocConfirm, setFinanceDeleteDocConfirm] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('bank-statement');
  const [taxBannerVisible, setTaxBannerVisible] = useState(false);
  const [taxBannerData, setTaxBannerData] = useState(null);
  const [reviewBannerVisible, setReviewBannerVisible] = useState(false);
  const [newReviewMonth, setNewReviewMonth] = useState('');
  const [unreadReviews, setUnreadReviews] = useState(0);
  const [activeTab, setActiveTab] = useState(initialTab || 'chat');
  const [activeReview, setActiveReview] = useState(null);
  const [reviewsHistory, setReviewsHistory] = useState([]);
  const [financialTimeline, setFinancialTimeline] = useState(() => cache.get('fin_timeline') || []);
  const [digilockerStatus, setDigilockerStatus] = useState(null); // null=loading, {connected,documents}
  const [digilockerConnecting, setDigilockerConnecting] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [advanceTaxAlert, setAdvanceTaxAlert] = useState(null);
  const [savingsAlert, setSavingsAlert] = useState(null);
  const [savingsAlertDismissed, setSavingsAlertDismissed] = useState(false);
  const [eightyCAlert, setEightyCAlert] = useState(null);
  const [crossDomainInsight, setCrossDomainInsight] = useState(null);
  const [intelligenceReports, setIntelligenceReports] = useState([]);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [salaryStagnationAlert, setSalaryStagnationAlert] = useState(null);
  const [insuranceAlert, setInsuranceAlert] = useState(null);
  const [raiseEmailCopied, setRaiseEmailCopied] = useState(false);
  const [insuranceAlertDismissed, setInsuranceAlertDismissed] = useState(
    () => !!localStorage.getItem('axigon_insurance_alert_dismissed')
  );

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [convsLoadError, setConvsLoadError] = useState(false);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const financeChatRef = useRef(null);
  const financeUploadRef = useRef(null);
  const financeExtractRef = useRef(null);

  // ── Utility ────────────────────────────────────────────────────────────────
  const relativeDate = (d) => {
    const date = new Date(d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = new Date(date); target.setHours(0, 0, 0, 0);
    const diff = Math.floor((today - target) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  // ── Data fetchers (cache-first) ────────────────────────────────────────────
  // Cache key prefix 'fin_' — all invalidated together after upload.
  // Conversations use a shorter TTL since they update on every chat send.

  const fetchFinanceConversations = async () => {
    const cached = cache.get('fin_conversations');
    if (cached) { setFinanceConversations(cached); setFinanceConvsLoading(false); return; }
    setFinanceConvsLoading(true);
    setConvsLoadError(false);
    try {
      const res = await fetch('/api/finance?action=unified-conversations', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const convs = data.conversations || [];
        cache.set('fin_conversations', convs, cache.TTL.SHORT);
        setFinanceConversations(convs);
      } else { setConvsLoadError(true); }
    } catch { setConvsLoadError(true); }
    setFinanceConvsLoading(false);
  };

  const fetchReviews = async () => {
    const cached = cache.get('fin_reviews');
    // Only use cache if it actually has reviews — don't serve a stale empty array
    if (cached && cached.length > 0) {
      setReviewsHistory(cached);
      setActiveReview(cached[0]);
      return;
    }
    setReviewsLoading(true);
    try {
      const res = await fetch('/api/finance?action=get-reviews', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const reviews = data.reviews || [];
        if (reviews.length > 0) cache.set('fin_reviews', reviews, cache.TTL.MEDIUM);
        setReviewsHistory(reviews);
        if (reviews.length > 0) setActiveReview(reviews[0]);
      }
    } catch {}
    setReviewsLoading(false);
  };

  const fetchTimeline = async () => {
    const cached = cache.get('fin_timeline');
    if (cached && cached.length > 0) { setFinancialTimeline(cached); return; }
    try {
      const res = await fetch('/api/finance?action=timeline', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const entries = data.timeline || [];
        if (entries.length > 0) cache.set('fin_timeline', entries, cache.TTL.MEDIUM);
        setFinancialTimeline(entries);
      }
    } catch {}
  };

  const fetchAdvanceTaxAlert = async () => {
    const cached = cache.get('fin_advance_tax');
    if (cached !== null) { setAdvanceTaxAlert(cached); return; }
    try {
      const r = await fetch('/api/finance?action=advance-tax-check', { credentials: 'include' });
      const d = await r.json();
      cache.set('fin_advance_tax', d.alert || null, cache.TTL.LONG);
      setAdvanceTaxAlert(d.alert || null);
    } catch {}
  };

  const fetchSavingsAlert = async () => {
    if (sessionStorage.getItem('axigon_savings_alert_dismissed')) return;
    const cached = cache.get('fin_savings_alert');
    if (cached !== null) { setSavingsAlert(cached); return; }
    try {
      const r = await fetch('/api/finance?action=savings-alert', { credentials: 'include' });
      const d = await r.json();
      cache.set('fin_savings_alert', d.alert || null, cache.TTL.MEDIUM);
      setSavingsAlert(d.alert || null);
    } catch {}
  };

  const fetchEightyCAlert = async () => {
    const year = new Date().getFullYear();
    if (localStorage.getItem(`axigon_80c_alert_${year}`)) return;
    const cached = cache.get('fin_80c_alert');
    if (cached !== null) { setEightyCAlert(cached); return; }
    try {
      const r = await fetch('/api/finance?action=80c-alert', { credentials: 'include' });
      const d = await r.json();
      cache.set('fin_80c_alert', d.alert || null, cache.TTL.LONG);
      setEightyCAlert(d.alert || null);
    } catch {}
  };

  const fetchCrossDomainInsight = async () => {
    const cached = cache.get('fin_cross_domain');
    if (cached !== null) { setCrossDomainInsight(cached); return; }
    try {
      const r = await fetch('/api/finance?action=cross-domain-insight', { credentials: 'include' });
      const d = await r.json();
      const val = d.insight ? d : null;
      cache.set('fin_cross_domain', val, cache.TTL.MEDIUM);
      setCrossDomainInsight(val);
    } catch {}
  };

  const fetchInsuranceAlert = async () => {
    if (localStorage.getItem('axigon_insurance_alert_dismissed')) return;
    const cached = cache.get('fin_insurance_alert');
    if (cached !== null) { setInsuranceAlert(cached); return; }
    try {
      const r = await fetch('/api/finance?action=insurance-alert', { credentials: 'include' });
      const d = await r.json();
      cache.set('fin_insurance_alert', d.alert || null, cache.TTL.LONG);
      setInsuranceAlert(d.alert || null);
    } catch {}
  };

  const fetchSalaryStagnationAlert = async () => {
    const cached = cache.get('fin_salary_stagnation');
    if (cached !== null) { setSalaryStagnationAlert(cached); return; }
    try {
      const r = await fetch('/api/finance?action=salary-stagnation-alert', { credentials: 'include' });
      const d = await r.json();
      cache.set('fin_salary_stagnation', d.alert || null, cache.TTL.LONG);
      setSalaryStagnationAlert(d.alert || null);
    } catch {}
  };

  const fetchIntelligenceReports = async () => {
    const cached = cache.get('fin_intelligence');
    if (cached) { setIntelligenceReports(cached); return; }
    setIntelligenceLoading(true);
    try {
      const r = await fetch('/api/finance?action=get-cross-domain-reports', { credentials: 'include' });
      const d = await r.json();
      const reports = d.reports || [];
      cache.set('fin_intelligence', reports, cache.TTL.MEDIUM);
      setIntelligenceReports(reports);
    } catch {}
    setIntelligenceLoading(false);
  };

  const fetchDigilockerStatus = async () => {
    try {
      const r = await fetch('/api/finance?action=digilocker-status', { credentials: 'include' });
      const d = await r.json();
      setDigilockerStatus(d);
    } catch {
      setDigilockerStatus({ connected: false });
    }
  };

  const handleDigilockerConnect = async () => {
    setDigilockerConnecting(true);
    try {
      const r = await fetch('/api/finance?action=digilocker-initiate', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!d.configured) {
        alert('DigiLocker integration is not yet configured. Check back soon.');
        return;
      }
      window.location.href = d.authUrl;
    } catch {
      alert('Failed to initiate DigiLocker connection. Please try again.');
    } finally {
      setDigilockerConnecting(false);
    }
  };

  const handleDigilockerDisconnect = async () => {
    if (!window.confirm('Disconnect DigiLocker? Your document list will be removed.')) return;
    try {
      await fetch('/api/finance?action=digilocker-disconnect', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      setDigilockerStatus({ connected: false });
    } catch {}
  };

  // ── Action handlers ────────────────────────────────────────────────────────
  const handleFinanceSend = async (e, forcedMsg) => {
    if (e && e.preventDefault) e.preventDefault();
    const msg = forcedMsg || financeInput.trim();
    if (!msg || financeLoading) return;
    setFinanceMessages(prev => [...prev, { role: 'user', text: msg }]);
    setFinanceInput('');
    setFinanceLoading(true);
    setFinanceChatError('');
    setLifeFinanceFirstTime(false);
    try {
      const res = await fetch('/api/finance?action=unified-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: msg, conversationId: financeConversationId }),
      });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      const reply = data.response || 'No response received.';
      if (data.conversationId) setFinanceConversationId(data.conversationId);
      setFinanceMessages(prev => [...prev, { role: 'assistant', text: reply }]);
      cache.invalidate('fin_conversations');
      fetchFinanceConversations();
    } catch {
      setFinanceChatError('Failed to get a response. Please try again.');
    } finally {
      setFinanceLoading(false);
    }
  };

  const handleFinanceUpload = async (file) => {
    if (!file) return;
    setLifeFinanceFirstTime(false);
    setFinanceUploadStatus('uploading');
    setFinanceUploadMessage('');

    // Bust all finance caches so fresh data loads after upload
    cache.invalidate('fin_');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 150000); // 150s client timeout

    try {
      const formData = new FormData();
      formData.append('file', file);
      setFinanceUploadStatus('processing');

      const safeFetch = async (url, opts) => {
        const res = await fetch(url, { ...opts, signal: controller.signal });
        let data;
        try { data = await res.json(); } catch { data = {}; }
        if (!res.ok) throw new Error(data.error || `Server error (${res.status})`);
        return data;
      };

      if (uploadDocType === 'salary-slip') {
        const data = await safeFetch('/api/finance?action=parse-salary-slip', {
          method: 'POST', credentials: 'include', body: formData,
        });
        setFinanceUploadStatus('done');
        const netPay = data.parsedData?.net_pay;
        const tds = data.parsedData?.tds_deducted;
        setFinanceUploadMessage(
          netPay != null
            ? `Salary slip parsed — Rs.${Number(netPay).toLocaleString('en-IN')} net pay, Rs.${Number(tds || 0).toLocaleString('en-IN')} TDS deducted.`
            : 'Salary slip parsed — your profile has been updated'
        );
        fetchFinanceProfile();
        fetchHealthScore();
        fetchFinanceDocuments();
        fetchTaxEstimate();
      } else if (uploadDocType === 'bank-statement') {
        const data = await safeFetch('/api/finance?action=upload', {
          method: 'POST', credentials: 'include', body: formData,
        });
        setFinanceUploadStatus('done');
        setFinanceUploadMessage(
          data.transactionCount
            ? `Bank statement processed — ${data.transactionCount} transactions found, profile updated.`
            : (data.summary || 'Bank statement processed — profile updated.')
        );
        fetchFinanceProfile();
        fetchFinanceDocuments();
        fetchHealthScore();
        fetchTimeline();
        fetchTaxEstimate();
        // Review generates async server-side — poll after a short delay to show banner
        setTimeout(() => {
          fetchReviews();
          const monthName = new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' });
          setNewReviewMonth(monthName);
          setReviewBannerVisible(true);
          setUnreadReviews(prev => prev + 1);
        }, 8000);
      } else {
        formData.append('docType', uploadDocType);
        await safeFetch('/api/finance?action=upload', {
          method: 'POST', credentials: 'include', body: formData,
        });
        setFinanceUploadStatus('done');
        setFinanceUploadMessage('Document saved. Ask me anything about it in the chat.');
        fetchFinanceProfile();
        fetchFinanceDocuments();
        fetchHealthScore();
      }
    } catch (err) {
      setFinanceUploadStatus('error');
      if (err.name === 'AbortError') {
        setFinanceUploadMessage('Upload timed out. The document may be too large or the server is busy — please try again.');
      } else {
        setFinanceUploadMessage(err.message || 'Upload failed. Please try again.');
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const handleExtractToExcel = async (file) => {
    if (!file) return;
    setFinanceUploadStatus('uploading');
    setFinanceUploadMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      setFinanceUploadStatus('processing');
      const res = await fetch('/api/finance?action=extract-statement', {
        method: 'POST', credentials: 'include', body: formData,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Extraction failed');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'statement_analysis.xlsx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setFinanceUploadStatus('done');
      setFinanceUploadMessage('Excel file downloaded — check your downloads folder');
    } catch (err) {
      setFinanceUploadStatus('error');
      setFinanceUploadMessage(err.message || 'Extraction failed. Please try again.');
    }
  };

  const handleFinanceDeleteConversation = async (convId) => {
    try {
      const res = await fetch('/api/finance?action=delete-unified-conversation', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conversationId: convId }),
      });
      if (res.ok) {
        setFinanceConversations(prev => prev.filter(c => c._id.toString() !== convId));
        if (financeConversationId === convId) {
          setFinanceConversationId(null);
          setFinanceMessages([]);
        }
      }
    } catch {}
    setFinanceDeleteConvConfirm(null);
  };

  const handleFinanceDeleteDocument = async (docId) => {
    try {
      const res = await fetch('/api/finance?action=delete-document', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ documentId: docId }),
      });
      if (res.ok) {
        const data = await res.json();
        setFinanceDocuments(prev => prev.filter(d => d._id.toString() !== docId));
        setFinanceProfile(data.profile || null);
      }
    } catch {}
    setFinanceDeleteDocConfirm(null);
  };

  // ── Effects ────────────────────────────────────────────────────────────────

  // Fetch all data on page mount — chat and profile first, analytics deferred
  useEffect(() => {
    // Priority: conversations and profile load immediately (user sees these first)
    fetchFinanceConversations();
    fetchFinanceProfile();
    fetchFinanceDocuments();
    fetchHealthScore();
    fetchReviews();
    fetchTimeline();
    fetchDigilockerStatus();
    // Handle DigiLocker OAuth return
    const params = new URLSearchParams(window.location.search);
    const dlStatus = params.get('digilocker');
    if (dlStatus === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (onTabConsumed) onTabConsumed();
    // Defer analytics — these are slower and not visible on initial render
    setTimeout(() => {
      fetchTaxEstimate();
      fetchAdvanceTaxAlert();
      fetchSavingsAlert();
      fetchEightyCAlert();
      fetchCrossDomainInsight();
      fetchIntelligenceReports();
      fetchSalaryStagnationAlert();
      fetchInsuranceAlert();
    }, 800);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // First-time greeting injection
  useEffect(() => {
    if (lifeFinanceFirstTime && financeMessages.length === 0) {
      const firstName = user?.name?.split(' ')[0] || 'there';
      setFinanceMessages([{
        role: 'assistant',
        text: `Hi ${firstName}, I'm Axigon — your unified AI advisor for money, career, and legal questions. Ask me anything, or upload a salary slip or bank statement and I'll analyze it and give you a clear picture of your finances.`,
      }]);
    }
  }, [lifeFinanceFirstTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll chat to bottom on new messages
  useEffect(() => {
    if (financeChatRef.current) financeChatRef.current.scrollTop = financeChatRef.current.scrollHeight;
  }, [financeMessages, financeLoading]);

  // Auto-dismiss error bar after 4s
  useEffect(() => {
    if (!financeChatError) return;
    const t = setTimeout(() => setFinanceChatError(''), 4000);
    return () => clearTimeout(t);
  }, [financeChatError]);

  // Auto-dismiss tax banner after 60s
  useEffect(() => {
    if (!taxBannerVisible) return;
    const t = setTimeout(() => {
      setTaxBannerVisible(false);
      localStorage.setItem('axigon_tax_banner_dismissed_date', new Date().toDateString());
    }, 60000);
    return () => clearTimeout(t);
  }, [taxBannerVisible]);

  // Show tax banner when taxData prop arrives (e.g. after upload)
  useEffect(() => {
    if (!taxData || !(taxData.savings > 0)) return;
    const today = new Date().toDateString();
    const dismissed = localStorage.getItem('axigon_tax_banner_dismissed_date');
    if (dismissed !== today) {
      setTaxBannerData(taxData);
      setTaxBannerVisible(true);
    }
  }, [taxData]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)', paddingTop: '64px', display: 'flex', position: 'relative' }}>

      {/* ── Mobile sidebar backdrop ── */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 40, top: '64px' }}
        />
      )}

      {/* ── Left Sidebar ── */}
      <div style={{
        width: '220px', flexShrink: 0, backgroundColor: 'var(--bg)',
        borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        ...(isMobile
          ? { position: 'fixed', top: '64px', left: 0, zIndex: 50, transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease', boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.5)' : 'none' }
          : { position: 'sticky', top: '64px' }
        ),
      }}>
        <div style={{ padding: '20px 16px 12px', borderBottom: '1px solid var(--border)' }}>
          <button
            onClick={() => goBack()}
            style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: 0, marginBottom: '48px', fontFamily: 'inherit', transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >← Back</button>
          <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '12px' }}>
            Ask Axigon <span style={{ background: 'linear-gradient(135deg, #10B981, var(--accent))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Finance</span>
          </div>
          <button
            onClick={() => {
              if (financeConversationId) {
                fetch('/api/finance?action=update-context', {
                  method: 'POST', credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ conversationId: financeConversationId, agentType: 'finance' }),
                }).catch(() => {});
              }
              setFinanceMessages([]); setFinanceConversationId(null); setFinanceInput(''); setFinanceUploadStatus(null); setFinanceUploadMessage('');
            }}
            style={{ width: '100%', padding: '9px', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer', transition: 'opacity 0.2s', fontFamily: 'inherit' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >+ New Chat</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {financeConvsLoading && (
            <>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: '8px', marginBottom: '2px' }}>
                  <div style={{ height: '11px', borderRadius: '4px', backgroundColor: 'var(--border)', marginBottom: '6px', width: `${60 + i * 10}%`, animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ height: '9px', borderRadius: '4px', backgroundColor: 'var(--bg-secondary)', width: '40%' }} />
                </div>
              ))}
            </>
          )}
          {!financeConvsLoading && convsLoadError && (
            <p style={{ color: '#F87171', fontSize: '12px', textAlign: 'center', marginTop: '24px', padding: '0 8px', lineHeight: 1.6 }}>Could not load conversations.<br /><button onClick={fetchFinanceConversations} style={{ color: '#10B981', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', padding: 0, fontFamily: 'inherit', marginTop: '4px' }}>Retry</button></p>
          )}
          {!financeConvsLoading && !convsLoadError && financeConversations.length === 0 && (
            <p style={{ color: 'var(--text-tertiary)', fontSize: '12px', textAlign: 'center', marginTop: '24px', padding: '0 8px', lineHeight: 1.6 }}>No conversations yet.<br />Upload a document or ask a finance question.</p>
          )}
          {!financeConvsLoading && financeConversations.map(conv => {
            const isActive = financeConversationId === conv._id.toString();
            const isConfirming = financeDeleteConvConfirm === conv._id.toString();
            const date = relativeDate(conv.updatedAt || conv.createdAt);
            return (
              <div key={conv._id} style={{ position: 'relative', marginBottom: '2px' }}
                onMouseEnter={e => { const btn = e.currentTarget.querySelector('.del-btn'); if (btn) btn.style.opacity = '1'; }}
                onMouseLeave={e => { const btn = e.currentTarget.querySelector('.del-btn'); if (btn && !isConfirming) btn.style.opacity = '0'; }}
              >
                {isConfirming ? (
                  <div style={{ padding: '8px 12px', borderRadius: '8px', backgroundColor: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.25)' }}>
                    <p style={{ fontSize: '12px', color: 'var(--text-primary)', marginBottom: '8px' }}>Delete this conversation?</p>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => handleFinanceDeleteConversation(conv._id.toString())}
                        style={{ flex: 1, padding: '5px', borderRadius: '5px', background: '#F43F5E', color: 'white', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Yes</button>
                      <button onClick={() => setFinanceDeleteConvConfirm(null)}
                        style={{ flex: 1, padding: '5px', borderRadius: '5px', background: 'var(--border)', color: 'var(--text-secondary)', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>No</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      setFinanceConversationId(conv._id.toString());
                      setFinanceMessagesLoading(true);
                      try {
                        const res = await fetch(`/api/finance?action=unified-messages&conversationId=${conv._id}`, { credentials: 'include' });
                        const data = await res.json();
                        setFinanceMessages((data.messages || []).map(m => ({ role: m.role, text: m.content })));
                        if (isMobile) setSidebarOpen(false);
                      } catch {
                        setFinanceChatError('Failed to load conversation messages.');
                      }
                      setFinanceMessagesLoading(false);
                    }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: '8px', border: 'none', backgroundColor: isActive ? 'rgba(16,185,129,0.12)' : 'transparent', cursor: 'pointer', transition: 'background-color 0.15s', outline: isActive ? '1px solid rgba(16,185,129,0.3)' : 'none', paddingRight: '28px', fontFamily: 'inherit' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 500, color: isActive ? '#6EE7B7' : 'var(--text-secondary)', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {conv.title.length > 35 ? conv.title.slice(0, 35) + '…' : conv.title}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{date}</div>
                  </button>
                )}
                {!isConfirming && (
                  <button className="del-btn"
                    onClick={e => { e.stopPropagation(); setFinanceDeleteConvConfirm(conv._id.toString()); }}
                    style={{ position: 'absolute', top: '50%', right: '6px', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '14px', cursor: 'pointer', padding: '2px 5px', borderRadius: '4px', opacity: 0, transition: 'opacity 0.15s, color 0.15s', fontFamily: 'inherit', lineHeight: 1 }}
                    onMouseEnter={e => e.currentTarget.style.color = '#F43F5E'}
                    onMouseLeave={e => e.currentTarget.style.color = '#4B6279'}
                  >×</button>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Monthly Reviews ── */}
        {(reviewsLoading || reviewsHistory.length > 0) && (
          <div style={{ borderTop: '1px solid var(--border)', flexShrink: 0 }}>
            <p style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0, padding: '12px 16px 8px' }}>Monthly Reviews</p>
            <div style={{ padding: '0 8px 8px' }}>
              {reviewsLoading ? (
                <div style={{ height: '10px', borderRadius: '5px', backgroundColor: 'var(--bg-secondary)', width: '70%', margin: '4px 4px 8px' }} />
              ) : (
                reviewsHistory.slice(0, 6).map(r => {
                  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                  const isActive = activeReview?._id === r._id;
                  const s = r.score;
                  const scoreColor = s >= 80 ? 'var(--accent)' : s >= 60 ? '#10B981' : s >= 40 ? '#F59E0B' : '#EF4444';
                  return (
                    <button key={r._id}
                      onClick={() => { setActiveReview(r); setActiveTab('summary'); }}
                      style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '6px', border: 'none', backgroundColor: isActive ? 'rgba(255,255,255,0.04)' : 'transparent', cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; e.currentTarget.querySelector('.rev-label').style.color = 'var(--text-primary)'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.querySelector('.rev-label').style.color = isActive ? 'var(--text-primary)' : 'var(--text-secondary)'; }}
                    >
                      <span className="rev-label" style={{ fontSize: '13px', color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)', transition: 'color 0.15s' }}>{monthNames[r.month - 1]} {r.year}</span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: scoreColor }}>{s}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Middle: Chat + Review ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', minWidth: 0 }}>

        {/* Mobile top bar */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0, backgroundColor: 'var(--bg-secondary)' }}>
            <button
              onClick={() => setSidebarOpen(o => !o)}
              style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)', borderRadius: '7px', padding: '6px 12px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <span style={{ fontSize: '15px', lineHeight: 1 }}>☰</span> Chats
            </button>
            <button
              onClick={() => { setFinanceMessages([]); setFinanceConversationId(null); setFinanceInput(''); setFinanceUploadStatus(null); setFinanceUploadMessage(''); }}
              style={{ background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'none', borderRadius: '7px', padding: '6px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
            >+ New</button>
          </div>
        )}

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: isMobile ? '0 12px' : '0 32px', gap: isMobile ? '16px' : '28px', flexShrink: 0, overflowX: 'auto' }}>
          {[{ key: 'chat', label: 'Chat' }, { key: 'summary', label: 'Financial Summary' }, { key: 'intelligence', label: 'Intelligence' }, { key: 'ca-docs', label: 'CA Documents' }].map(({ key, label }) => (
            <button key={key}
              onClick={() => { setActiveTab(key); if (key === 'summary') setUnreadReviews(0); }}
              onMouseEnter={e => { if (activeTab !== key) e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={e => { if (activeTab !== key) e.currentTarget.style.color = 'var(--text-tertiary)'; }}
              style={{
                background: 'none', border: 'none',
                borderBottom: activeTab === key ? '2px solid var(--text-primary)' : '2px solid transparent',
                color: activeTab === key ? 'var(--text-primary)' : 'var(--text-tertiary)',
                fontSize: '13px', fontWeight: activeTab === key ? 600 : 400, padding: '14px 0 12px',
                cursor: 'pointer', fontFamily: 'inherit', marginBottom: '-1px', transition: 'color 0.2s',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {label}
              {key === 'summary' && unreadReviews > 0 && (
                <span style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#10B981', color: 'white', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, lineHeight: 1 }}>{unreadReviews}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Financial Summary Panel (Monarch-style) ── */}
        {activeTab === 'summary' && (() => {
          // Empty state
          if (!financeProfile && !activeReview) {
            return (
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', padding: '0 32px', maxWidth: '400px' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(0,194,124,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: '#00C27C', letterSpacing: '0.04em' }}>LF</span>
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>No financial data yet</h3>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 24px' }}>
                    Upload a bank statement or salary slip to see your financial summary.
                  </p>
                  <button
                    onClick={() => setActiveTab('chat')}
                    style={{ padding: '10px 24px', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'none', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}
                  >Upload Now</button>
                </div>
              </div>
            );
          }

          const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

          // Data from profile
          const income = financeProfile?.monthlyIncome || financeProfile?.income || 0;
          const expenses = financeProfile?.totalExpenses || financeProfile?.expenses || 0;
          const balance = financeProfile?.currentBalance || financeProfile?.balance || 0;
          const savingsRate = income > 0 ? Math.round(((income - expenses) / income) * 100) : null;
          const cashFlowMax = Math.max(income, expenses, 1);
          const incomeWidth = Math.round((income / cashFlowMax) * 100);
          const expensesWidth = Math.round((expenses / cashFlowMax) * 100);

          // Categories from profile
          const rawCategories = financeProfile?.expenseCategories || financeProfile?.expenses?.categories || {};
          const categoryColors = { rent: '#6366F1', food: '#F97316', transport: '#EAB308', entertainment: '#A855F7', salary: '#10B981', utilities: '#0EA5E9', others: '#6B7280', shopping: '#EC4899', health: '#14B8A6' };
          const sortedCats = Object.entries(rawCategories).sort((a, b) => b[1] - a[1]).slice(0, 5);
          const catTotal = sortedCats.reduce((s, [, v]) => s + v, 0) || 1;

          // Transactions
          const txns = (financeProfile?.transactions || []).slice(-8).reverse();

          // Savings goal calculations
          const travelGoal = 50000;
          const homeGoal = 500000;
          const emergencyGoal = income * 6;
          const travelMonthly = income > 0 ? Math.max(0, Math.round((travelGoal - balance) / 12)) : null;
          const homeMonthly = income > 0 ? Math.max(0, Math.round((homeGoal - balance) / 36)) : null;
          const emergencyGap = income > 0 ? Math.max(0, emergencyGoal - balance) : null;

          // Active review data
          const sec = activeReview?.sections || {};
          const reviewMonthLabel = activeReview ? `${monthNames[(activeReview.month || 1) - 1]} ${activeReview.year}` : null;
          const s = activeReview?.score;
          const scoreColor = s != null ? (s >= 80 ? 'var(--accent)' : s >= 60 ? '#10B981' : s >= 40 ? '#F59E0B' : '#EF4444') : null;
          const scoreBg = s != null ? (s >= 80 ? 'rgba(0,194,124,0.1)' : s >= 60 ? 'rgba(16,185,129,0.1)' : s >= 40 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)') : null;

          const fmt = (n) => n != null ? `\u20B9${Number(n).toLocaleString('en-IN')}` : '—';

          return (
            <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#FAFAFA' }}>
              <div style={{ maxWidth: '900px', margin: '0 auto', padding: '28px 28px 40px' }}>

                {/* Month picker chips */}
                {reviewsHistory.length > 1 && (
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    {reviewsHistory.map(r => {
                      const isChipActive = r._id === activeReview?._id;
                      return (
                        <button key={r._id} onClick={() => setActiveReview(r)} style={{
                          fontSize: '12px', fontWeight: isChipActive ? 600 : 400, padding: '5px 14px',
                          borderRadius: '20px', border: `1px solid ${isChipActive ? 'var(--accent)' : 'var(--border)'}`,
                          background: isChipActive ? 'rgba(0,194,124,0.08)' : 'white',
                          color: isChipActive ? 'var(--accent)' : 'var(--text-secondary)',
                          cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                        }}>{monthNames[(r.month || 1) - 1].slice(0, 3)} {r.year}</button>
                      );
                    })}
                  </div>
                )}

                {/* Stat cards row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                  {[
                    { abbr: 'IN', accentColor: '#10B981', label: 'Monthly Income', value: fmt(income || null), sub: income > 0 ? null : 'Upload salary slip' },
                    { abbr: 'EX', accentColor: '#F97316', label: 'Monthly Expenses', value: fmt(expenses || null), sub: expenses > 0 ? null : 'Upload bank statement' },
                    { abbr: 'SR', accentColor: '#6366F1', label: 'Savings Rate', value: savingsRate != null ? `${savingsRate}%` : '—', sub: savingsRate != null ? (savingsRate >= 20 ? 'Healthy' : savingsRate >= 10 ? 'Moderate' : 'Low') : 'No data yet' },
                  ].map(({ abbr, accentColor, label, value, sub }) => (
                    <div key={label} style={{ backgroundColor: 'white', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 18px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '7px', backgroundColor: `${accentColor}15`, border: `1px solid ${accentColor}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: accentColor, letterSpacing: '0.04em' }}>{abbr}</span>
                      </div>
                      <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>{label}</p>
                      <p style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.01em' }}>{value}</p>
                      {sub && <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0 }}>{sub}</p>}
                    </div>
                  ))}
                </div>

                {/* ── Financial Trends (timeline data) ── */}
                {financialTimeline.length >= 2 && (() => {
                  const tl = financialTimeline.slice(-6); // last 6 months
                  const maxIncome   = Math.max(...tl.map(e => e.monthlyIncome  || 0), 1);
                  // eslint-disable-next-line no-unused-vars
                  const maxExpenses = Math.max(...tl.map(e => e.totalExpenses  || 0), 1);
                  const mnths = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

                  return (
                    <div style={{ backgroundColor: 'white', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 22px', marginBottom: '16px' }}>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 18px' }}>Trends — last {tl.length} months</p>

                      {/* Income vs Expenses bar chart */}
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '80px', marginBottom: '8px' }}>
                        {tl.map((entry, idx) => {
                          const incH = Math.round(((entry.monthlyIncome || 0) / maxIncome) * 72);
                          const expH = Math.round(((entry.totalExpenses || 0) / maxIncome) * 72);
                          return (
                            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '72px' }}>
                                <div title={`Income: ${fmt(entry.monthlyIncome)}`} style={{ width: '10px', height: `${incH || 2}px`, borderRadius: '3px 3px 0 0', backgroundColor: '#10B981', transition: 'height 0.4s ease' }} />
                                <div title={`Expenses: ${fmt(entry.totalExpenses)}`} style={{ width: '10px', height: `${expH || 2}px`, borderRadius: '3px 3px 0 0', backgroundColor: '#F97316', opacity: 0.75, transition: 'height 0.4s ease' }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Month labels */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                        {tl.map((entry, idx) => (
                          <div key={idx} style={{ flex: 1, textAlign: 'center', fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: 500 }}>
                            {mnths[(entry.month || 1) - 1]}
                          </div>
                        ))}
                      </div>

                      {/* Legend */}
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        {[{ color: '#10B981', label: 'Income' }, { color: '#F97316', label: 'Expenses' }].map(({ color, label }) => (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: color }} />
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{label}</span>
                          </div>
                        ))}
                      </div>

                      {/* Savings rate & health score sparkline row */}
                      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '4px' }}>Savings Rate</span>
                        {tl.map((entry, idx) => {
                          const sr = entry.savingsRate;
                          const color = sr == null ? 'var(--border)' : sr >= 20 ? '#10B981' : sr >= 10 ? '#F59E0B' : '#EF4444';
                          return (
                            <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, color }}>{sr != null ? `${sr}%` : '—'}</span>
                              <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>{mnths[(entry.month || 1) - 1]}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Health score trend */}
                      {tl.some(e => e.healthScore != null) && (
                        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px', marginTop: '10px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '4px' }}>Health Score</span>
                          {tl.map((entry, idx) => {
                            const hs = entry.healthScore;
                            const color = hs == null ? 'var(--border)' : hs >= 80 ? '#10B981' : hs >= 60 ? '#F59E0B' : '#EF4444';
                            return (
                              <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, color }}>{hs != null ? hs : '—'}</span>
                                <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>{mnths[(entry.month || 1) - 1]}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Savings goal cards */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
                  {[
                    { abbr: 'TF', accentColor: '#6366F1', title: 'Travel Fund', detail: income > 0 ? `Save ${fmt(travelMonthly)}/month to reach ${fmt(travelGoal)} in 12 months` : 'Upload salary slip to unlock' },
                    { abbr: 'HD', accentColor: '#0EA5E9', title: 'Home Down Payment', detail: income > 0 ? `Save ${fmt(homeMonthly)}/month to reach ${fmt(homeGoal)} in 3 years` : 'Upload salary slip to unlock' },
                    { abbr: 'EF', accentColor: '#10B981', title: 'Emergency Fund', detail: income > 0 ? (emergencyGap && emergencyGap > 0 ? `You need ${fmt(emergencyGap)} more (6x income)` : 'Goal reached! 6x income covered') : 'Upload salary slip to unlock' },
                  ].map(({ abbr, accentColor, title, detail }) => (
                    <div key={title} style={{ flexShrink: 0, width: '220px', backgroundColor: 'white', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: `${accentColor}15`, border: `1px solid ${accentColor}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: accentColor, letterSpacing: '0.04em' }}>{abbr}</span>
                      </div>
                      <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>{title}</p>
                      <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>{detail}</p>
                    </div>
                  ))}
                </div>

                {/* Cash flow section */}
                {(income > 0 || expenses > 0) && (
                  <div style={{ backgroundColor: 'white', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 22px', marginBottom: '16px' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>Cash Flow</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {[
                        { label: 'Income', value: income, width: incomeWidth, color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
                        { label: 'Expenses', value: expenses, width: expensesWidth, color: '#F97316', bg: 'rgba(249,115,22,0.08)' },
                      ].map(({ label, value, width, color, bg }) => (
                        <div key={label}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>{label}</span>
                            <span style={{ fontSize: '12px', fontWeight: 700, color }}>{fmt(value)}</span>
                          </div>
                          <div style={{ height: '10px', borderRadius: '6px', backgroundColor: bg, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${width}%`, borderRadius: '6px', backgroundColor: color, transition: 'width 0.6s ease' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bottom two-col layout */}
                <div style={{ display: 'grid', gridTemplateColumns: sortedCats.length > 0 ? '1fr 1fr' : '1fr', gap: '12px', marginBottom: '16px' }}>

                  {/* Top spending categories */}
                  {sortedCats.length > 0 && (
                    <div style={{ backgroundColor: 'white', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 22px' }}>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>Top Spending</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {sortedCats.map(([cat, amt]) => {
                          const pct = Math.round((amt / catTotal) * 100);
                          const color = categoryColors[cat.toLowerCase()] || categoryColors.others;
                          const label = cat.charAt(0).toUpperCase() + cat.slice(1);
                          return (
                            <div key={cat}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
                                <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 600 }}>{fmt(amt)}</span>
                              </div>
                              <div style={{ height: '6px', borderRadius: '4px', backgroundColor: 'var(--bg-secondary)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pct}%`, borderRadius: '4px', backgroundColor: color }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* AI Insight + Action */}
                  {(sec.topInsight || sec.oneAction || activeReview) && (
                    <div style={{ backgroundColor: 'white', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 22px' }}>
                      {reviewMonthLabel && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>AI Insight</p>
                          {s != null && (
                            <span style={{ fontSize: '12px', fontWeight: 700, color: scoreColor, backgroundColor: scoreBg, padding: '3px 10px', borderRadius: '20px' }}>{s}/100 · {reviewMonthLabel}</span>
                          )}
                        </div>
                      )}
                      {sec.topInsight && (
                        <div style={{ borderLeft: '3px solid var(--accent)', borderRadius: '0 8px 8px 0', padding: '12px 14px', backgroundColor: 'rgba(0,194,124,0.04)', marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.7, margin: 0 }}>{sec.topInsight}</p>
                        </div>
                      )}
                      {sec.oneAction && (
                        <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                          <div style={{ width: '14px', height: '14px', border: '1.5px solid var(--border)', borderRadius: '3px', flexShrink: 0, marginTop: '2px' }} />
                          <div>
                            <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>Action</p>
                            <p style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.6, margin: 0 }}>{sec.oneAction}</p>
                          </div>
                        </div>
                      )}
                      {!sec.topInsight && !sec.oneAction && !activeReview && (
                        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', lineHeight: 1.6, margin: 0 }}>Upload a bank statement to generate your first AI insight.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Recent Transactions */}
                {txns.length > 0 && (
                  <div style={{ backgroundColor: 'white', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 22px' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>Recent Transactions</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                      {txns.map((tx, idx) => {
                        const isCredit = tx.type === 'credit' || (tx.amount > 0 && tx.type !== 'debit');
                        const amtColor = isCredit ? '#10B981' : '#EF4444';
                        const amtPrefix = isCredit ? '+' : '-';
                        const txDate = tx.date ? relativeDate(tx.date) : '';
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: idx < txns.length - 1 ? '1px solid var(--border)' : 'none', gap: '12px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description || tx.narration || 'Transaction'}</p>
                              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0 }}>{txDate}</p>
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: amtColor, flexShrink: 0 }}>{amtPrefix}{fmt(Math.abs(tx.amount))}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            </div>
          );
        })()}

        {/* ── Intelligence Panel ── */}
        {activeTab === 'intelligence' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {intelligenceLoading ? (
              <div style={{ padding: '32px 40px', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '680px', margin: '0 auto' }}>
                {[70, 50, 85, 60].map(w => (
                  <div key={w} style={{ height: '12px', borderRadius: '6px', backgroundColor: 'var(--bg-secondary)', width: `${w}%` }} />
                ))}
              </div>
            ) : intelligenceReports.length === 0 ? (
              <div style={{ textAlign: 'center', marginTop: '80px', padding: '0 32px' }}>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '8px' }}>
                  Your first Intelligence Report generates after your next document upload.
                </p>
                <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
                  Upload a salary slip or bank statement to get started.
                </p>
              </div>
            ) : (() => {
              const rpt = intelligenceReports[0].report;
              const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              return (
                <div style={{ padding: '32px 40px', maxWidth: '720px', margin: '0 auto' }}>

                  {/* History selector */}
                  {intelligenceReports.length > 1 && (
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', flexWrap: 'wrap' }}>
                      {intelligenceReports.map((r, idx) => {
                        const isActive = idx === 0;
                        return (
                          <button key={r._id} onClick={() => {}} style={{
                            fontSize: '12px', fontWeight: 500, padding: '4px 12px',
                            borderRadius: '20px', border: `1px solid ${isActive ? 'var(--border)' : 'var(--border)'}`,
                            background: isActive ? 'var(--bg-secondary)' : 'transparent',
                            color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                            cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                          }}>{monthNames[r.month - 1].slice(0, 3)} {r.year}</button>
                        );
                      })}
                    </div>
                  )}

                  {/* Headline */}
                  {rpt.headline && (
                    <div style={{ marginBottom: '28px' }}>
                      <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>This month's intelligence</p>
                      <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.5, margin: 0 }}>{rpt.headline}</h2>
                    </div>
                  )}

                  {/* 2×2 domain cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                    {[
                      { label: 'Finance',          content: rpt.financeSummary },
                      { label: 'Career',            content: rpt.careerSummary },
                      { label: 'Legal',             content: rpt.legalSummary },
                      { label: 'Cross-Domain',      content: rpt.crossDomainInsight },
                    ].map(({ label, content }) => content && (
                      <div key={label} style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', padding: '16px' }}>
                        <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 8px' }}>{label}</p>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>{content}</p>
                      </div>
                    ))}
                  </div>

                  {/* Priority Action */}
                  {rpt.priorityAction && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '16px 20px', display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ width: '16px', height: '16px', border: '1px solid var(--border)', borderRadius: '3px', flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px' }}>Priority action</p>
                        <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.7, margin: 0 }}>{rpt.priorityAction}</p>
                      </div>
                    </div>
                  )}

                  {/* Positive Signal */}
                  {rpt.positiveSignal && (
                    <div style={{ backgroundColor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: '8px', padding: '14px 18px' }}>
                      <p style={{ fontSize: '10px', fontWeight: 700, color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px' }}>Going well</p>
                      <p style={{ fontSize: '13px', color: '#6EE7B7', lineHeight: 1.6, margin: 0 }}>{rpt.positiveSignal}</p>
                    </div>
                  )}

                </div>
              );
            })()}
          </div>
        )}

        {/* ── CA Documents Panel ── */}
        {activeTab === 'ca-docs' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <CADocuments embedded />
          </div>
        )}

        {/* ── Chat Panel ── */}
        {activeTab === 'chat' && <>

        {/* Error bar */}
        {financeChatError && (
          <div style={{ padding: '10px 24px', backgroundColor: 'rgba(244,63,94,0.12)', borderBottom: '1px solid rgba(244,63,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ color: '#FB7185', fontSize: '13px', fontWeight: 500 }}>{financeChatError}</span>
            <button onClick={() => setFinanceChatError('')} style={{ background: 'none', border: 'none', color: '#FB7185', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '0 4px', fontFamily: 'inherit' }}>×</button>
          </div>
        )}

        {/* First-time banner */}
        {lifeFinanceFirstTime && (
          <div style={{ padding: '12px 24px', backgroundColor: 'rgba(16,185,129,0.07)', borderBottom: '1px solid rgba(16,185,129,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#6EE7B7', lineHeight: 1.5, flex: 1 }}>
              Start by uploading a salary slip or bank statement — it takes 30 seconds and unlocks your full financial picture.
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <button
                onClick={() => { if (financeUploadRef.current) financeUploadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                style={{ padding: '6px 14px', borderRadius: '6px', backgroundColor: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)', color: '#6EE7B7', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(16,185,129,0.25)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(16,185,129,0.15)'}
              >Upload now</button>
              <button onClick={() => setLifeFinanceFirstTime(false)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '0 2px', fontFamily: 'inherit' }}>×</button>
            </div>
          </div>
        )}

        {/* Tax estimate banner */}
        {taxBannerVisible && taxBannerData && (
          <div style={{
            background: 'rgba(99,102,241,0.08)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderLeft: '3px solid var(--accent)',
            padding: '12px 20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: '16px', flexShrink: 0,
          }}>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#818CF8', margin: '0 0 2px' }}>Your tax estimate is ready</p>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                {taxBannerData.recommendation === 'new'
                  ? `Switch to new regime and save Rs.${Number(taxBannerData.savings).toLocaleString('en-IN')} this year`
                  : `Old regime saves you Rs.${Number(taxBannerData.savings).toLocaleString('en-IN')} this year`}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <button
                onClick={() => {
                  setTaxBannerVisible(false);
                  localStorage.setItem('axigon_tax_banner_dismissed_date', new Date().toDateString());
                }}
                style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '0 2px', fontFamily: 'inherit' }}
              >×</button>
            </div>
          </div>
        )}

        {/* Review-ready banner */}
        {reviewBannerVisible && newReviewMonth && (
          <div style={{
            background: 'rgba(16,185,129,0.07)',
            borderLeft: '3px solid #10B981',
            padding: '12px 20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: '16px', flexShrink: 0,
          }}>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#6EE7B7', margin: '0 0 2px' }}>Your {newReviewMonth} review is ready</p>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>See what changed this month</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <button
                onClick={() => { setActiveTab('summary'); setReviewBannerVisible(false); setUnreadReviews(0); }}
                style={{ background: 'none', border: '1px solid rgba(16,185,129,0.4)', borderRadius: '6px', color: '#6EE7B7', fontSize: '12px', fontWeight: 600, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(16,185,129,0.12)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >Read it →</button>
              <button
                onClick={() => setReviewBannerVisible(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '0 2px', fontFamily: 'inherit' }}
              >×</button>
            </div>
          </div>
        )}

        {/* Messages area */}
        <div ref={financeChatRef} style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px' : '28px 32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Upload prompt — always visible until a doc is uploaded */}
          {!financeProfile && (() => {
            const docTypes = [
              { key: 'salary-slip',    abbr: 'SS', accentColor: '#10B981', label: 'Salary Slip',      desc: 'Extract income, TDS & deductions' },
              { key: 'bank-statement', abbr: 'BS', accentColor: '#3B82F6', label: 'Bank Statement',    desc: 'Categorize spending & cash flow' },
              { key: 'form-16',        abbr: 'F16', accentColor: '#6366F1', label: 'Form 16',           desc: 'Annual TDS certificate from employer' },
              { key: 'assets',         abbr: 'AS', accentColor: '#0EA5E9', label: 'List of Assets',    desc: 'Investments, property & savings' },
              { key: 'debts',          abbr: 'DB', accentColor: '#F97316', label: 'List of Debts',     desc: 'Loans, EMIs & credit card dues' },
            ];
            return (
              <div ref={financeUploadRef} style={{ backgroundColor: 'var(--bg-secondary)', border: '1px dashed var(--border)', borderRadius: '12px', padding: '28px', marginBottom: '8px' }}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 14px', textAlign: 'center' }}>What would you like to upload?</p>
                {/* Doc type card grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                  {docTypes.map(({ key, abbr, accentColor, label, desc }) => {
                    const isActive = uploadDocType === key;
                    return (
                      <button
                        key={key}
                        onClick={() => {
                          setUploadDocType(key);
                          setFinanceUploadStatus(null);
                          setFinanceUploadMessage('');
                          financeUploadRef.current?.querySelector('input[type="file"]')?.click();
                        }}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                          padding: '14px', borderRadius: '12px', cursor: 'pointer',
                          backgroundColor: isActive ? 'rgba(0,194,124,0.06)' : 'white',
                          border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                          textAlign: 'left', fontFamily: 'inherit', transition: 'border-color 0.15s, background-color 0.15s',
                        }}
                        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = 'var(--accent)'; } }}
                        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = 'var(--border)'; } }}
                      >
                        <div style={{ width: '30px', height: '30px', borderRadius: '7px', backgroundColor: `${accentColor}14`, border: `1px solid ${accentColor}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                          <span style={{ fontSize: '10px', fontWeight: 700, color: accentColor, letterSpacing: '0.03em' }}>{abbr}</span>
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: isActive ? 'var(--accent)' : 'var(--text-primary)', display: 'block', marginBottom: '4px' }}>{label}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>{desc}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Drop zone (compact) */}
                <label
                  style={{ display: 'block', border: '1px dashed var(--border)', borderRadius: '10px', padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s, background-color 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#10B981'; e.currentTarget.style.backgroundColor = 'rgba(16,185,129,0.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#10B981'; e.currentTarget.style.backgroundColor = 'rgba(16,185,129,0.06)'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFinanceUpload(f); }}
                >
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleFinanceUpload(e.target.files[0]); }} />
                  {financeUploadStatus === null && (
                    <>
                      <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', marginBottom: '4px' }}>Drag & drop or click to browse</p>
                      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>PDF, JPG, PNG · Select a card above to pick type</span>
                    </>
                  )}
                  {financeUploadStatus === 'uploading' && <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Uploading...</p>}
                  {financeUploadStatus === 'processing' && <p style={{ color: '#10B981', fontSize: '13px' }}>Processing with AI...</p>}
                  {financeUploadStatus === 'error' && <p style={{ color: '#F43F5E', fontSize: '13px' }}>{financeUploadMessage}</p>}
                </label>

                {/* Extract to Excel link — bank statement only */}
                {uploadDocType === 'bank-statement' && financeUploadStatus === null && (
                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <label style={{ cursor: 'pointer' }}>
                      <input ref={financeExtractRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleExtractToExcel(e.target.files[0]); }} />
                      <span
                        style={{ fontSize: '12px', color: 'var(--text-tertiary)', transition: 'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
                      >Extract to Excel instead →</span>
                    </label>
                  </div>
                )}

                {financeUploadStatus === 'done' && (
                  <div style={{ marginTop: '14px', padding: '12px 16px', borderRadius: '8px', backgroundColor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#6EE7B7', fontSize: '13px', fontWeight: 500 }}>
                    {financeUploadMessage}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Upload strip when profile exists — compact doc type cards */}
          {financeProfile && (() => {
            const compactDocTypes = [
              { key: 'salary-slip',    abbr: 'SS',  accentColor: '#10B981', label: 'Salary Slip'    },
              { key: 'bank-statement', abbr: 'BS',  accentColor: '#3B82F6', label: 'Bank Statement' },
              { key: 'form-16',        abbr: 'F16', accentColor: '#6366F1', label: 'Form 16'        },
              { key: 'assets',         abbr: 'AS',  accentColor: '#0EA5E9', label: 'Assets'         },
              { key: 'debts',          abbr: 'DB',  accentColor: '#F97316', label: 'Debts'          },
            ];
            return (
              <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px', marginBottom: '4px' }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Upload another document</p>
                <input
                  ref={financeUploadRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files[0]) handleFinanceUpload(e.target.files[0]); e.target.value = ''; }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                  {compactDocTypes.map(({ key, abbr, accentColor, label }) => {
                    const isActive = uploadDocType === key;
                    return (
                      <button
                        key={key}
                        onClick={() => {
                          setUploadDocType(key);
                          setFinanceUploadStatus(null);
                          setFinanceUploadMessage('');
                          if (financeUploadRef.current) financeUploadRef.current.click();
                        }}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '10px 6px', borderRadius: '8px', cursor: 'pointer',
                          backgroundColor: isActive ? 'rgba(0,194,124,0.06)' : 'var(--bg)',
                          border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                          fontFamily: 'inherit', transition: 'border-color 0.15s, background-color 0.15s', gap: '4px',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = 'var(--accent)'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = 'var(--border)'; }}
                      >
                        <div style={{ width: '24px', height: '24px', borderRadius: '5px', backgroundColor: isActive ? 'rgba(0,194,124,0.15)' : `${accentColor}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '9px', fontWeight: 700, color: isActive ? 'var(--accent)' : accentColor, letterSpacing: '0.03em' }}>{abbr}</span>
                        </div>
                        <span style={{ fontSize: '10px', fontWeight: 600, color: isActive ? 'var(--accent)' : 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
                      </button>
                    );
                  })}
                </div>
                {(financeUploadStatus === 'uploading' || financeUploadStatus === 'processing') && (
                  <p style={{ fontSize: '12px', color: '#10B981', margin: '8px 0 0', textAlign: 'center' }}>Processing...</p>
                )}
                {financeUploadStatus === 'done' && (
                  <p style={{ fontSize: '12px', color: '#6EE7B7', margin: '8px 0 0', textAlign: 'center' }}>{financeUploadMessage}</p>
                )}
                {financeUploadStatus === 'error' && (
                  <p style={{ fontSize: '12px', color: '#F43F5E', margin: '8px 0 0', textAlign: 'center' }}>{financeUploadMessage}</p>
                )}
              </div>
            );
          })()}

          {/* Loading conversation state */}
          {financeMessagesLoading && (
            <div style={{ textAlign: 'center', marginTop: '80px', color: 'var(--text-tertiary)', fontSize: '14px' }}>Loading conversation...</div>
          )}

          {/* Empty chat state */}
          {!financeMessagesLoading && financeMessages.length === 0 && (
            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <p style={{ color: 'var(--text-tertiary)', fontSize: '15px', marginBottom: '8px' }}>Ask anything about your money, career, or legal matters</p>
              <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', marginBottom: '24px' }}>SIP, PPF, budgeting, ITR, tax planning, salary benchmarking, contracts, and more</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '420px', margin: '0 auto' }}>
                {[
                  'How much should I save each month for retirement?',
                  'Am I being paid fairly for my role and experience?',
                  'How can I reduce my income tax this year?',
                ].map(q => (
                  <button key={q} onClick={() => handleFinanceSend(null, q)}
                    style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.2s, background-color 0.2s', fontFamily: 'inherit' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(16,185,129,0.4)'; e.currentTarget.style.backgroundColor = 'rgba(16,185,129,0.04)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'; }}
                  >{q}</button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {!financeMessagesLoading && financeMessages.map((msg, i) => {
            if (msg.role === 'user') {
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ maxWidth: '72%', padding: '12px 16px', borderRadius: '16px 16px 4px 16px', backgroundColor: '#10B981', color: '#fff', fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {msg.text}
                  </div>
                </div>
              );
            }
            // Assistant — parse structured sections
            const sections = [];
            const raw = msg.text;
            const sectionLabels = ['Summary', 'Analysis', 'Action Items'];
            let remaining = raw;
            const found = [];
            sectionLabels.forEach(label => {
              const re = new RegExp(`${label}:\\s*`, 'i');
              const idx = remaining.search(re);
              if (idx !== -1) found.push({ label, idx: raw.indexOf(remaining.slice(idx)) });
            });
            found.sort((a, b) => a.idx - b.idx);

            if (found.length === 0) {
              sections.push({ label: null, content: raw });
            } else {
              found.forEach((f, fi) => {
                const start = f.idx + f.label.length + 1;
                const end = found[fi + 1] ? found[fi + 1].idx : raw.length;
                sections.push({ label: f.label, content: raw.slice(start).slice(raw.slice(start).search(/\S/), end - start).trim() });
              });
            }

            const sectionColors = { 'Summary': '#10B981', 'Analysis': 'var(--accent)', 'Action Items': 'var(--accent)' };
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {sections.map((sec, si) => (
                    <div key={si} style={{ padding: '12px 16px', borderRadius: si === 0 ? '16px 16px 4px 4px' : si === sections.length - 1 ? '4px 4px 16px 4px' : '4px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                      {sec.label && (
                        <div style={{ fontSize: '11px', fontWeight: 700, color: sectionColors[sec.label] || 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>{sec.label}</div>
                      )}
                      <div style={{ color: 'var(--text-primary)', fontSize: '14px', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{sec.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {!financeMessagesLoading && financeLoading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ padding: '12px 18px', borderRadius: '16px 16px 16px 4px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-tertiary)', fontSize: '14px' }}>Thinking...</div>
            </div>
          )}
        </div>

        {/* Input bar */}
        <div style={{ padding: isMobile ? '12px 16px' : '18px 32px', borderTop: '1px solid var(--border)', backgroundColor: 'var(--bg)' }}>
          <form onSubmit={handleFinanceSend} style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              placeholder="Ask about your finances..."
              value={financeInput}
              onChange={e => setFinanceInput(e.target.value)}
              disabled={financeLoading}
              style={{ flex: 1, padding: '13px 16px', borderRadius: '10px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', fontFamily: 'inherit', opacity: financeLoading ? 0.6 : 1, transition: 'border-color 0.2s' }}
              onFocus={e => e.currentTarget.style.borderColor = '#10B981'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
            />
            <button type="submit" disabled={financeLoading || !financeInput.trim()}
              style={{ padding: '13px 22px', borderRadius: '10px', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'none', fontWeight: 700, fontSize: '14px', cursor: financeLoading || !financeInput.trim() ? 'not-allowed' : 'pointer', opacity: financeLoading || !financeInput.trim() ? 0.5 : 1, transition: 'opacity 0.2s', whiteSpace: 'nowrap', fontFamily: 'inherit' }}
            >Send</button>
          </form>
        </div>
        </>}

      </div>

      {/* ── Right Panel: Financial Summary + Documents ── */}
      <div style={{ width: '260px', flexShrink: 0, backgroundColor: 'var(--bg-secondary)', borderLeft: '1px solid var(--border)', display: isMobile ? 'none' : 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', position: 'sticky', top: '64px', overflowY: 'auto' }}>

        {/* Health Score */}
        <div style={{ padding: '20px 16px 12px', borderBottom: '1px solid var(--border)' }}>
          <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Financial Health</p>
        </div>
        <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
          <HealthScore healthScore={financeHealthScore} scoreLoading={financeScoreLoading} />
        </div>

        {/* Connected Insight */}
        {crossDomainInsight?.insight && (
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderTop: '3px solid #7C3AED', borderRadius: '0 0 8px 8px', padding: '16px', margin: '0 8px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#7C3AED', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10B981', display: 'inline-block', flexShrink: 0 }} />
              <p style={{ fontSize: '10px', fontWeight: 600, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Connected Insight</p>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.7, margin: '0 0 10px' }}>{crossDomainInsight.insight}</p>
            {crossDomainInsight.actionItem && (
              <>
                <p style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>Suggested action</p>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.55 }}>{crossDomainInsight.actionItem}</p>
              </>
            )}
            <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', margin: '10px 0 0' }}>Based on your finance and legal activity</p>
          </div>
        )}

        {/* Do This Month */}
        {(() => {
          const traj = financeHealthScore?.components?.emergencyFund?.trajectory;
          const showTransfer = traj?.status === 'building' && traj?.monthlyContribution;
          const showRaise = !!salaryStagnationAlert;
          if (!showTransfer && !showRaise) return null;

          const raiseEmail = salaryStagnationAlert ? [
            `Subject: Compensation Review Discussion`,
            ``,
            `Hi [Manager's name],`,
            ``,
            `I hope you're doing well. I'd like to schedule time to discuss my compensation.`,
            ``,
            `I've been at the same salary level for ${salaryStagnationAlert.monthsFlat} months. Based on market data for my role, I'm currently Rs.${salaryStagnationAlert.marketGap != null ? Math.abs(salaryStagnationAlert.marketGap).toLocaleString('en-IN') : '—'} below the benchmark, and inflation has reduced my real income by Rs.${Number(salaryStagnationAlert.inflationLoss).toLocaleString('en-IN')} in this period.`,
            ``,
            `I'd appreciate 30 minutes to discuss how we can align my compensation with my contributions and current market rates.`,
            ``,
            `Looking forward to connecting.`,
            ``,
            `Best regards,`,
            `[Your name]`,
          ].join('\n') : '';

          return (
            <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', background: 'rgba(245,158,11,0.03)' }}>
              <p style={{ fontSize: '10px', fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>Do This Month</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {showTransfer && (
                  <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '8px', padding: '12px 14px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 700, color: '#10B981', margin: '0 0 3px' }}>
                      Transfer Rs.{Number(traj.monthlyContribution).toLocaleString('en-IN')} to emergency fund
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                      Rs.{Number(traj.gapAmount).toLocaleString('en-IN')} gap · target by {traj.targetDate}
                    </p>
                  </div>
                )}
                {showRaise && (
                  <div style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: '8px', padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <p style={{ fontSize: '13px', fontWeight: 700, color: '#7C3AED', margin: 0 }}>Send your raise request</p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(raiseEmail).then(() => {
                            setRaiseEmailCopied(true);
                            setTimeout(() => setRaiseEmailCopied(false), 2000);
                          });
                        }}
                        style={{ background: raiseEmailCopied ? 'rgba(16,185,129,0.15)' : 'rgba(124,58,237,0.15)', border: `1px solid ${raiseEmailCopied ? '#10B981' : 'rgba(124,58,237,0.4)'}`, borderRadius: '5px', color: raiseEmailCopied ? '#10B981' : '#A78BFA', fontSize: '11px', fontWeight: 700, padding: '3px 9px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s', flexShrink: 0 }}
                      >{raiseEmailCopied ? '✓ Copied' : 'Copy email'}</button>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                      Pre-filled draft — paste into email and send to your manager
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Active Alerts */}
        {(advanceTaxAlert || (savingsAlert && !savingsAlertDismissed) || eightyCAlert || salaryStagnationAlert || financeHealthScore?.components?.emergencyFund?.trajectory?.status === 'declining' || (insuranceAlert && !insuranceAlertDismissed)) && (
          <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
            <p style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>Active Alerts</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <AlertCard type="advance-tax" data={advanceTaxAlert} onDismiss={null} />
              <AlertCard
                type="savings-drop"
                data={savingsAlert && !savingsAlertDismissed ? savingsAlert : null}
                onDismiss={() => { setSavingsAlertDismissed(true); sessionStorage.setItem('axigon_savings_alert_dismissed', '1'); }}
              />
              <AlertCard
                type="80c"
                data={eightyCAlert}
                onDismiss={() => { setEightyCAlert(null); localStorage.setItem(`axigon_80c_alert_${new Date().getFullYear()}`, '1'); }}
              />
              {salaryStagnationAlert && (
                <div style={{ background: 'rgba(124,58,237,0.06)', borderLeft: '3px solid #7C3AED', borderRadius: '6px', padding: '14px 16px' }}>
                  <p style={{ fontSize: '10px', fontWeight: 600, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>Salary Stagnation</p>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>No raise in {salaryStagnationAlert.monthsFlat} months</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 4px', lineHeight: 1.5 }}>
                    Inflation has reduced your real income by Rs.{Number(salaryStagnationAlert.inflationLoss).toLocaleString('en-IN')} in this period
                  </p>
                  {salaryStagnationAlert.marketGap != null && salaryStagnationAlert.marketGap < 0 && (
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 6px', lineHeight: 1.5 }}>
                      Also Rs.{Math.abs(salaryStagnationAlert.marketGap).toLocaleString('en-IN')} below market rate
                    </p>
                  )}
                  <button
                    onClick={() => navigateTo('career-co-pilot')}
                    style={{ background: 'none', border: 'none', color: '#7C3AED', fontSize: '12px', fontWeight: 500, cursor: 'pointer', padding: 0, fontFamily: 'inherit', transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >See career benchmark →</button>
                </div>
              )}
              {financeHealthScore?.components?.emergencyFund?.trajectory?.status === 'declining' && (
                <div style={{ background: 'rgba(239,68,68,0.06)', borderLeft: '3px solid #EF4444', borderRadius: '6px', padding: '14px 16px' }}>
                  <p style={{ fontSize: '10px', fontWeight: 600, color: '#B91C1C', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>Emergency Fund Declining</p>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>Expenses exceeding income this month</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>Your safety net is shrinking. Review expenses.</p>
                </div>
              )}
              {insuranceAlert && !insuranceAlertDismissed
                && !(savingsAlert && !savingsAlertDismissed)
                && financeHealthScore?.components?.emergencyFund?.trajectory?.status !== 'declining'
                && (
                <div style={{ background: 'rgba(16,185,129,0.05)', borderLeft: '3px solid #059669', borderRadius: '6px', padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <p style={{ fontSize: '10px', fontWeight: 600, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>Insurance Gap Detected</p>
                    <button
                      onClick={() => { setInsuranceAlertDismissed(true); localStorage.setItem('axigon_insurance_alert_dismissed', '1'); }}
                      style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 0 0 8px', fontFamily: 'inherit' }}
                    >×</button>
                  </div>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>No term insurance found on file</p>
                  <p style={{ fontSize: '14px', color: '#6EE7B7', margin: '0 0 2px', fontWeight: 600 }}>Recommended cover: Rs.{insuranceAlert.coverInCrores} Cr</p>
                  <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: '0 0 4px' }}>Estimated premium: Rs.{Number(insuranceAlert.estimatedPremium).toLocaleString('en-IN')}/year</p>
                  <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', margin: 0 }}>Term insurance protects your family if your income stops.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tax Estimate shortcut — only when data exists */}
        {taxData && !taxData.error && (
          <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
            <p style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>Tax Estimate</p>
            <p style={{ fontSize: '13px', color: 'var(--text-primary)', margin: '0 0 8px', lineHeight: 1.5 }}>
              {taxData.recommendation === 'new'
                ? `New regime saves Rs.${Number(taxData.savings).toLocaleString('en-IN')}`
                : `Old regime saves Rs.${Number(taxData.savings).toLocaleString('en-IN')}`}
            </p>
          </div>
        )}

        {/* Financial Summary — only when profile exists */}
        {financeProfile && (
          <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
            <p style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Financial Summary</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'Monthly Income',   value: financeProfile.monthlyIncome },
                { label: 'Annual Income',    value: financeProfile.annualIncome },
                { label: 'Monthly Expenses', value: financeProfile.totalExpenses },
                { label: 'Current Balance',  value: financeProfile.currentBalance },
              ].map(({ label, value }) => value != null && (
                <div key={label}>
                  <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '2px' }}>{label}</p>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Rs.{Number(value).toLocaleString('en-IN')}</p>
                </div>
              ))}
              {financeProfile.lastUpdated && (
                <div>
                  <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '2px' }}>Last Updated</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>{new Date(financeProfile.lastUpdated).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* DigiLocker */}
        <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
            <span style={{ fontSize: '14px' }}>🇮🇳</span>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>DigiLocker</p>
          </div>
          {digilockerStatus === null ? (
            <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Checking…</p>
          ) : digilockerStatus.connected ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#10B981', display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#10B981' }}>Connected</span>
              </div>
              {Array.isArray(digilockerStatus.documents) && digilockerStatus.documents.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '10px' }}>
                  {digilockerStatus.documents.slice(0, 5).map((doc, i) => (
                    <div key={i} style={{ padding: '6px 9px', borderRadius: '6px', backgroundColor: 'var(--bg)', border: '1px solid var(--border)' }}>
                      <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name || doc.type || 'Document'}</p>
                      {doc.doctype && <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', margin: 0 }}>{doc.doctype}</p>}
                    </div>
                  ))}
                  {digilockerStatus.documents.length > 5 && (
                    <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '2px 0 0' }}>+{digilockerStatus.documents.length - 5} more</p>
                  )}
                </div>
              ) : (
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px', lineHeight: 1.5 }}>No issued documents found in your DigiLocker</p>
              )}
              <button
                onClick={handleDigilockerDisconnect}
                style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '11px', cursor: 'pointer', padding: 0, fontFamily: 'inherit', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#F43F5E'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
              >Disconnect →</button>
            </>
          ) : (
            <>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px', lineHeight: 1.55 }}>
                Import your Aadhaar, PAN, Form 16 and other govt-issued documents directly.
              </p>
              <button
                onClick={handleDigilockerConnect}
                disabled={digilockerConnecting}
                style={{ width: '100%', padding: '9px', borderRadius: '8px', background: digilockerConnecting ? 'var(--border)' : 'linear-gradient(135deg, #1E3A8A, #1D4ED8)', color: digilockerConnecting ? 'var(--text-tertiary)' : 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: digilockerConnecting ? 'not-allowed' : 'pointer', transition: 'opacity 0.2s', fontFamily: 'inherit' }}
                onMouseEnter={e => { if (!digilockerConnecting) e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                {digilockerConnecting ? 'Connecting…' : 'Connect DigiLocker'}
              </button>
            </>
          )}
        </div>

        {/* Uploaded Documents */}
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border)' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Uploaded Documents</p>
          {financeDocuments.length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)', fontSize: '12px', lineHeight: 1.6 }}>No documents uploaded yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {financeDocuments.map(doc => {
                const isConfirming = financeDeleteDocConfirm === doc._id.toString();
                const shortName = doc.fileName.length > 25 ? doc.fileName.slice(0, 25) + '…' : doc.fileName;
                const docDate = new Date(doc.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                return (
                  <div key={doc._id}
                    style={{ padding: '8px 10px', borderRadius: '7px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
                    onMouseEnter={e => { const btn = e.currentTarget.querySelector('.doc-del'); if (btn) btn.style.opacity = '1'; }}
                    onMouseLeave={e => { const btn = e.currentTarget.querySelector('.doc-del'); if (btn && !isConfirming) btn.style.opacity = '0'; }}
                  >
                    {isConfirming ? (
                      <>
                        <p style={{ fontSize: '11px', color: 'var(--text-primary)', marginBottom: '7px' }}>Delete this document?</p>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button onClick={() => handleFinanceDeleteDocument(doc._id.toString())}
                            style={{ flex: 1, padding: '4px', borderRadius: '4px', background: '#F43F5E', color: 'white', border: 'none', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Yes</button>
                          <button onClick={() => setFinanceDeleteDocConfirm(null)}
                            style={{ flex: 1, padding: '4px', borderRadius: '4px', background: 'var(--border)', color: 'var(--text-secondary)', border: 'none', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>No</button>
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '6px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortName}</p>
                          <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0 }}>{doc.documentType.replace('_', ' ')} · {docDate}</p>
                        </div>
                        <button className="doc-del"
                          onClick={() => setFinanceDeleteDocConfirm(doc._id.toString())}
                          style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '14px', cursor: 'pointer', padding: '0 2px', opacity: 0, transition: 'opacity 0.15s, color 0.15s', fontFamily: 'inherit', lineHeight: 1, flexShrink: 0 }}
                          onMouseEnter={e => e.currentTarget.style.color = '#F43F5E'}
                          onMouseLeave={e => e.currentTarget.style.color = '#4B6279'}
                        >×</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

    </section>
  );
};

export default LifeFinance;
